import React from "react"
import { composeSx } from "../theme/composeSx"
import { Typography, type Theme, type TypographyProps } from "@mui/material"
import { colors, typeScale } from "../theme/tokens"

export type ViraTextTone =
  | "ink"
  | "muted"
  | "subtle"
  | "lavender"
  | "positive"
  | "danger"

export type ViraTextProps = TypographyProps & {
  tone?: ViraTextTone
}

function toneColor(theme: Theme, tone: ViraTextTone): string {
  const vira = theme.palette.vira ?? colors.vira
  switch (tone) {
    case "ink":
      return vira.ink
    case "muted":
      return vira.muted
    case "subtle":
      return vira.subtle
    case "lavender":
      return vira.lavenderText
    case "positive":
      return colors.graphs.alerts.positive
    case "danger":
      return colors.secondary.crimson
  }
}

export function ViraText({
  tone = "muted",
  sx,
  children,
  ...rest
}: ViraTextProps) {
  return (
    <Typography
      sx={composeSx((theme) => ({
        color: toneColor(theme, tone)}), sx)}
      {...rest}
    >
      {children}
    </Typography>
  )
}

export type HeadingLevel = "page" | "section" | "card" | "eyebrow" | "label"

export type HeadingProps = Omit<TypographyProps, "variant"> & {
  level?: HeadingLevel
  tone?: ViraTextTone
}

function headingSx(theme: Theme, level: HeadingLevel): object {
  const vira = theme.palette.vira ?? colors.vira
  const ink = vira.ink
  const subtle = vira.subtle
  switch (level) {
    case "page":
      return {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        fontSize: typeScale.page,
        letterSpacing: "-0.54px",
        color: ink,
      }
    case "section":
    case "card":
      // Same size — hierarchy is placement (outside vs inside card), not px spam.
      return {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        fontSize: typeScale.heading,
        letterSpacing: "-0.15px",
        color: ink,
      }
    case "eyebrow":
    case "label":
      return {
        fontSize: typeScale.meta,
        fontWeight: level === "label" ? 600 : 400,
        letterSpacing: level === "label" ? "1.52px" : undefined,
        textTransform: level === "label" ? "uppercase" : undefined,
        color: subtle,
      }
  }
}

function headingComponent(
  level: HeadingLevel,
): "h1" | "h2" | "h3" | "p" {
  switch (level) {
    case "page":
      return "h1"
    case "section":
      return "h2"
    case "card":
      return "h3"
    case "eyebrow":
    case "label":
      return "p"
  }
}

export function Heading({
  level = "section",
  tone,
  sx,
  children,
  ...rest
}: HeadingProps) {
  return (
    <Typography
      component={headingComponent(level)}
      sx={composeSx((theme) => ({
        ...headingSx(theme, level),
        ...(tone ? { color: toneColor(theme, tone) } : null)}), sx)}
      {...rest}
    >
      {children}
    </Typography>
  )
}
